import { CourseCapability, OrgCapability, SectionCapability } from "../shared/capabilities";

import { useAppSelector } from "store/index";
import {
  selectCourseCapabilities,
  selectCourseCapabilitiesByCourseId,
  selectOrgCapabilities,
  selectPageCapabilities,
  selectSectionCapabilities,
  selectSectionCapabilitiesBySectionId,
  selectSectionCapabilitiesByUserId,
  selectSectionUserIdsWithSectionCapability,
  selectUserIdsWhoCanAuthorSection,
  selectUserIdsWhoCanParticipateInSection,
} from "store/selectors/permissions";
import { useListSelector } from "store/store-hooks";

/**
 * Hook to get all section-level capabilities for the current user in the current section and
 * all org-level capabilities for the current user in the current org.
 *
 * @returns An object containing all capabilities
 *
 * @example
 * ```tsx
 * const capabilities = useCapabilities();
 * // Returns: { canAuthorOrg: false, canAuthorSection: true, ... }
 * ```
 */
export const useCapabilities = () => {
  const sectionCapabilities = useAppSelector(selectSectionCapabilities);
  const orgCapabilities = useAppSelector(selectOrgCapabilities);
  const courseCapabilities = useAppSelector(selectCourseCapabilities);

  return { ...sectionCapabilities, ...orgCapabilities, ...courseCapabilities } as Record<
    SectionCapability | OrgCapability | CourseCapability,
    boolean
  >;
};

/**
 * Hook to get all capabilities for a specific user in the current section.
 *
 * @param userId - The ID of the user to get capabilities for
 * @returns An object containing the user's capabilities
 *
 * @example
 * ```tsx
 * const userCapabilities = useSectionCapabilitiesByUserId('user123');
 * // Returns: { canAuthorSection: false, canParticipateInSection: true, ... }
 * ```
 */
export const useSectionCapabilitiesByUserId = (userId: string) => {
  // Note: currently, this only contains section capabilities. If needed, we can add org capabilities here
  // similarly to useCapabilities.
  return useAppSelector((state) => selectSectionCapabilitiesByUserId(state, userId));
};

/**
 * Hook to get a list of section user IDs that have a specific capability.
 *
 * @param capability - The capability to filter section users by
 * @returns An array of section user IDs that have the specified capability
 */
export const useSectionUserIdsWithSectionCapability = (capability: SectionCapability) => {
  return useListSelector((state) => selectSectionUserIdsWithSectionCapability(state, capability));
};

/**
 * Hook to get all section-level capabilities for the current user in a specific section.
 *
 * @param sectionId - The ID of the section to get capabilities for
 * @returns An object containing the section's capabilities
 *
 * @example
 * ```tsx
 * const sectionCapabilities = useSectionCapabilitiesBySectionId('section456');
 * // Returns: { canAuthorSection: false, canParticipateInSection: true, ... }
 * ```
 */
export const useSectionCapabilitiesBySectionId = (sectionId: string | null) => {
  return useAppSelector((state) => selectSectionCapabilitiesBySectionId(state, sectionId));
};

/**
 * Hook to get all course-level capabilities for the current user in a specific course.
 * Mirrors the server's has_course_capability/get_user_role_in_course: a
 * course_admin org role only yields canAuthorCourse when the user also has a CourseUser
 * or CourseGroupingUser row for the given course (or its grouping).
 *
 * @param courseId - The ID of the course to get capabilities for
 * @returns An object containing the course's capabilities
 *
 * @example
 * ```tsx
 * const courseCapabilities = useCourseCapabilitiesByCourseId('course123');
 * // Returns: { canAuthorCourse: true }
 * ```
 */
export const useCourseCapabilitiesByCourseId = (courseId: string | null) => {
  return useAppSelector((state) => selectCourseCapabilitiesByCourseId(state, courseId));
};

/**
 * Hook to get a list of user IDs for the users that can participate in the current section.
 *
 * @returns An array of user IDs
 */
export const useUserIdsWhoCanParticipateInSection = () => {
  return useListSelector(selectUserIdsWhoCanParticipateInSection);
};

/**
 * Hook to get a list of user IDs for the users that can author the current section.
 *
 * @returns An array of user IDs
 */
export const useUserIdsWhoCanAuthorSection = () => {
  return useListSelector(selectUserIdsWhoCanAuthorSection);
};

/**
 * Hook to get all page-level capabilities for the current user on a specific page.
 *
 * @param pageId - The ID of the page to get capabilities for
 * @returns An object containing the page's capabilities
 */
export const usePageCapabilitiesForPageId = (pageId: string | null) => {
  return useAppSelector((state) => selectPageCapabilities(state, pageId));
};
